// Конфиг приложения: GET /api/v5/mobile/config/ — первый запрос (гид §1.9). Панель вкладок, шапки, меню, лимиты
// избранного. Токен приложения не принят (401 invalid_access_token) — экран «Приложение недоступно» без повторов
// (shouldRetry не ретраит 4xx; при фокусе конфиг сам не перезапрашивается). refetchConfig() — перечитать (меню, жест
// «потянуть вниз»).
import { useQuery } from '@tanstack/react-query';
import { apiGet } from './client';
import { queryClient } from './queryClient';
import type { HeaderItem } from '../ui/AppBar';
import type { TabBarItem } from '../ui/TabBar';

export type { TabBarItem };

/** Пункт меню настроек (config.navigation.menu): незнакомый action.type пропускается (HQ-271). */
export interface MenuItem {
  key: string; label: string; icon?: string; subtitle?: string; requires?: string;
  action: { type: string; endpoint?: string; setting?: string; address?: string; url_key?: string };
}
export interface MenuSection { key: string; items: MenuItem[] }

export interface AppConfig {
  locale?: string;
  config_contract_version?: number;
  supported_languages?: string[];
  features?: Record<string, boolean>;
  /** потолки избранного по типам (HQ-269) */
  favorite_limits?: Record<string, number>;
  navigation?: {
    version?: number;
    tab_bar?: { items?: TabBarItem[]; default_key?: string };
    /** Шапки вкладок: `visible: false` — шапку не рисовать; пустая без флага — рисуем свою. */
    headers?: Record<string, { items?: HeaderItem[]; visible?: boolean }>;
    menu?: { sections?: MenuSection[] };
  };
  [key: string]: unknown;
}

export const CONFIG_PATH = '/api/v5/mobile/config/';
export const CONFIG_KEY = ['config'] as const;

export function useConfig() {
  return useQuery({
    queryKey: CONFIG_KEY,
    // без signal: отменять первый запрос приложения незачем (в dev StrictMode отмена дала бы второй запрос)
    queryFn: () => apiGet<AppConfig>(CONFIG_PATH),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Перечитать конфиг (меню: pull-to-refresh). */
export function refetchConfig(): Promise<unknown> {
  return queryClient.refetchQueries({ queryKey: CONFIG_KEY });
}

/** Вкладки, которые умеет приложение (Р-3: незнакомый ключ — молча пропустить). У каждой свой экран (src/App.tsx
 *  TAB_SCREENS): matches — матч-центр W1, остальные — модули W2–W3 (пока заглушки «Скоро»). */
export const KNOWN_TABS = ['matches', 'leagues', 'favorites', 'news', 'highlights'] as const;
export const SCREEN_TABS: ReadonlySet<string> = new Set(KNOWN_TABS);
/** Вкладка, на которой открывается приложение, если сервер не назвал видимую. */
export const FALLBACK_TAB = 'matches';

export interface ResolvedTabs { items: TabBarItem[]; defaultKey: string }

/** Панель из ответа: порядок и подписи сервера; незнакомые ключи и дубли — мимо; пустая подпись — своя запасная.
 *  default_key не из видимых — первая вкладка; панели нет (пустой items) — открывается матч-центр. */
export function resolveTabs(config: AppConfig | undefined, fallbackLabel: (key: string) => string): ResolvedTabs {
  const bar = config?.navigation?.tab_bar;
  const seen = new Set<string>();
  const items: TabBarItem[] = [];
  for (const item of bar?.items ?? []) {
    if (!item || typeof item.key !== 'string' || !(KNOWN_TABS as readonly string[]).includes(item.key) || seen.has(item.key)) continue;
    seen.add(item.key);
    items.push({ ...item, label: item.label || fallbackLabel(item.key), icon: item.icon ?? item.key });
  }
  const defaultKey = items.some((i) => i.key === bar?.default_key) ? bar!.default_key! : items[0]?.key ?? FALLBACK_TAB;
  return { items, defaultKey };
}
